/**
 * API Client with Offline Cache Fallback
 *
 * Wraps fetch to cache responses and fall back to cached data when API fails (FR-032).
 * Enables read-only mode using cached data when Google Sheets API is unavailable.
 * Uses VITE_API_BASE_URL when set (e.g. http://localhost:3001/api) for direct backend calls.
 */

import { offlineCacheService } from './offline-cache.service';
import { offlineQueueService } from './offline-queue.service';
import { CACHE_KEYS, CACHE_EXPIRATION_MS } from '../config/cache-config';
import { getApiUrl } from '../config/api';

export interface ApiResponseWithCache<T> {
  data: T;
  fromCache: boolean;
  readOnlyMode: boolean;
}

const CACHE_KEY_MAP: Record<string, { key: string; expiration: number }> = {
  '/api/residents': { key: CACHE_KEYS.RESIDENTS, expiration: CACHE_EXPIRATION_MS.residents },
  '/api/payments': { key: CACHE_KEYS.PAYMENTS, expiration: CACHE_EXPIRATION_MS.payments },
  '/api/expenses': { key: CACHE_KEYS.EXPENSES, expiration: CACHE_EXPIRATION_MS.expenses },
  '/api/beds': { key: CACHE_KEYS.BEDS, expiration: CACHE_EXPIRATION_MS.beds },
  '/api/reports/dashboard': { key: CACHE_KEYS.DASHBOARD, expiration: CACHE_EXPIRATION_MS.dashboard },
  '/api/reports/profit-loss': { key: CACHE_KEYS.REPORTS_TRENDS, expiration: CACHE_EXPIRATION_MS.reportsTrends },
};

function getCacheConfig(url: string): { key: string; expiration: number } | null {
  for (const [prefix, config] of Object.entries(CACHE_KEY_MAP)) {
    if (url.includes(prefix) && !url.includes('/beds/')) {
      return config;
    }
  }
  if (url.includes('/residents') && !url.includes('/residents/')) {
    return { key: CACHE_KEYS.RESIDENTS, expiration: CACHE_EXPIRATION_MS.residents };
  }
  return null;
}

/**
 * Fetch with cache fallback. On API failure, returns cached data if available (read-only mode).
 */
export async function fetchWithCacheFallback<T>(
  url: string,
  options?: RequestInit
): Promise<ApiResponseWithCache<T>> {
  const cacheConfig = getCacheConfig(url);

  try {
    const response = await fetch(getApiUrl(url), {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('auth_token') && {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        }),
        ...options?.headers,
      },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = (await response.json()) as T;

    if (cacheConfig && (options?.method === 'GET' || !options?.method)) {
      offlineCacheService.set(cacheConfig.key, data, cacheConfig.expiration);
      offlineCacheService.remove(CACHE_KEYS.READ_ONLY_MODE);
    }

    return { data, fromCache: false, readOnlyMode: false };
  } catch (error) {
    if (cacheConfig && (options?.method === 'GET' || !options?.method)) {
      const cached = offlineCacheService.get<T>(cacheConfig.key);
      if (cached) {
        offlineCacheService.set(CACHE_KEYS.READ_ONLY_MODE, true, 60 * 60 * 1000);
        return { data: cached, fromCache: true, readOnlyMode: true };
      }
    }
    throw error;
  }
}

/**
 * Check if currently in read-only mode (using cached data due to API failure).
 */
export function isReadOnlyMode(): boolean {
  return offlineCacheService.get<boolean>(CACHE_KEYS.READ_ONLY_MODE) === true;
}

/**
 * Submit mutation (POST/PUT/DELETE) - queues when offline.
 */
export async function submitWithOfflineQueue(
  endpoint: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  payload?: unknown
): Promise<void> {
  if (navigator.onLine) {
    const response = await fetch(getApiUrl(endpoint), {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(localStorage.getItem('auth_token') && {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        }),
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  } else {
    offlineQueueService.enqueue({
      type: method === 'POST' ? 'CREATE' : method === 'DELETE' ? 'DELETE' : 'UPDATE',
      entity: endpoint.split('/')[2] || 'unknown',
      endpoint,
      method,
      payload,
    });
  }
}
